import { lazy, Suspense, type FC } from 'react'
import type { RouteObject } from 'react-router'
import { Navigate, Outlet } from 'react-router-dom'
import { wrapWithErrorBoundary } from './wrapWithErrorBoundary'
import { SettingsLayout } from '@/layouts/settings'
import { FeatureFlagRoute } from '@/components/FeatureFlagRoute'
import { FeatureNames } from '@/lib/featureFlags'
import { paths } from '@/paths'
import { AuthGuard } from '@/guards/auth-guard'
import { ORG_SETTINGS_ROLES, BILLING_SETTINGS_ROLES } from '@/sections/settings/org-settings-access'

const AccountProfilePage = lazy(() => import('@/pages/settings/profile'))
const AccountNotificationsPage = lazy(
  () => import('@/pages/settings/notifications')
)
const AccountSecurityPage = lazy(() => import('@/pages/settings/security'))
const AccountOrgPage = lazy(() => import('@/pages/settings/org'))
const AccountFormCustomizationPage = lazy(
  () => import('@/pages/settings/formulaire')
)
const AccountAgenciesPage = lazy(() => import('@/pages/settings/agencies'))
const AccountAgencyDetailPage = lazy(
  () => import('@/pages/settings/agency-detail')
)
const AccountMembersPage = lazy(() => import('@/pages/settings/members'))
const AccountMemberDetailPage = lazy(
  () => import('@/pages/settings/member-detail')
)
const AccountRolesPage = lazy(() => import('@/pages/settings/roles'))
const AccountRoleDetailPage = lazy(() => import('@/pages/settings/role-detail'))
const AccountEmailTemplatesPage = lazy(
  () => import('@/pages/settings/email-templates')
)
const AccountHelpPage = lazy(() => import('@/pages/settings/help'))
const AccountAiAgentsPage = lazy(() => import('@/pages/settings/ai-agents'))
const AccountSubscriptionPage = lazy(
  () => import('@/pages/settings/subscription')
)
const AccountBillingPage = lazy(() => import('@/pages/settings/billing'))

const wrap = (C: React.ComponentType) => {
  const Wrapped = wrapWithErrorBoundary(C)
  return (
    <Suspense>
      <Wrapped />
    </Suspense>
  )
}

const OrgSettingsRoutesOutlet: FC = () => (
  <AuthGuard role={ORG_SETTINGS_ROLES}>
    <Suspense>
      <Outlet />
    </Suspense>
  </AuthGuard>
)

const HyperlineSubscriptionPage: FC = () => (
  <FeatureFlagRoute
    flag={FeatureNames.hyperline}
    enabled={() => (
      <AuthGuard role={BILLING_SETTINGS_ROLES}>
        {wrap(AccountSubscriptionPage)}
      </AuthGuard>
    )}
    disabled={() => (
      <Navigate to={paths.dashboard.settings.org.general} replace />
    )}
  />
)

const HyperlineBillingPage: FC = () => (
  <FeatureFlagRoute
    flag={FeatureNames.hyperline}
    enabled={() => (
      <AuthGuard role={BILLING_SETTINGS_ROLES}>
        {wrap(AccountBillingPage)}
      </AuthGuard>
    )}
    disabled={() => (
      <Navigate to={paths.dashboard.settings.org.general} replace />
    )}
  />
)

export const settingsNestedRoute: RouteObject = {
  path: 'settings',
  element: (
    <FeatureFlagRoute
      flag={FeatureNames.newSettings}
      enabled={SettingsLayout}
      disabled={() => <Navigate to={paths.dashboard.index} replace />}
    />
  ),
  children: [
    {
      index: true,
      element: <Navigate to="account/profile" replace />,
    },
    {
      path: 'account',
      element: <Navigate to="/settings/account/profile" replace />,
    },
    {
      path: 'account/profile',
      element: wrap(AccountProfilePage),
      handle: {
        documentTitle: { namespace: 'settings', key: 'docTitleProfile' },
      },
    },
    {
      path: 'account/notifications',
      element: wrap(AccountNotificationsPage),
      handle: {
        documentTitle: { namespace: 'settings', key: 'docTitleNotifications' },
      },
    },
    {
      path: 'account/security',
      element: wrap(AccountSecurityPage),
      handle: {
        documentTitle: { namespace: 'settings', key: 'docTitleSecurity' },
      },
    },
    {
      path: 'org',
      element: <OrgSettingsRoutesOutlet />,
      children: [
        {
          index: true,
          element: <Navigate to="/settings/org/general" replace />,
        },
        {
          path: 'general',
          element: wrap(AccountOrgPage),
          handle: {
            documentTitle: { namespace: 'settings', key: 'docTitleOrg' },
          },
        },
        {
          path: 'formulaire',
          element: wrap(AccountFormCustomizationPage),
          handle: {
            documentTitle: {
              namespace: 'settings',
              key: 'docTitleFormCustomization',
            },
          },
        },
        {
          path: 'agencies',
          element: wrap(AccountAgenciesPage),
          handle: {
            documentTitle: { namespace: 'settings', key: 'docTitleAgencies' },
          },
        },
        {
          path: 'agencies/:groupId',
          element: wrap(AccountAgencyDetailPage),
          handle: {
            documentTitle: {
              namespace: 'settings',
              key: 'docTitleAgencyDetail',
            },
          },
        },
        {
          path: 'members',
          element: wrap(AccountMembersPage),
          handle: {
            documentTitle: { namespace: 'settings', key: 'docTitleMembers' },
          },
        },
        {
          path: 'members/:userId',
          element: wrap(AccountMemberDetailPage),
          handle: {
            documentTitle: {
              namespace: 'settings',
              key: 'docTitleMemberDetail',
            },
          },
        },
        {
          path: 'roles',
          element: wrap(AccountRolesPage),
          handle: {
            documentTitle: { namespace: 'settings', key: 'docTitleRoles' },
          },
        },
        {
          path: 'roles/:roleId',
          element: wrap(AccountRoleDetailPage),
          handle: {
            documentTitle: { namespace: 'settings', key: 'docTitleRoles' },
          },
        },
        {
          path: 'email-templates',
          element: wrap(AccountEmailTemplatesPage),
          handle: {
            documentTitle: {
              namespace: 'settings',
              key: 'docTitleEmailTemplates',
            },
          },
        },
        {
          path: 'subscription',
          element: <HyperlineSubscriptionPage />,
          handle: {
            documentTitle: {
              namespace: 'settings',
              key: 'docTitleSubscription',
            },
          },
        },
        {
          path: 'billing',
          element: <HyperlineBillingPage />,
          handle: {
            documentTitle: {
              namespace: 'settings',
              key: 'docTitleBilling',
            },
          },
        },
      ],
    },
    {
      path: 'modules/nabu-assistant',
      element: (
        <FeatureFlagRoute
          flag={FeatureNames.smartPreparation}
          enabled={() => wrap(AccountAiAgentsPage)}
          disabled={() => <Navigate to={paths.dashboard.index} replace />}
        />
      ),
      handle: {
        documentTitle: { namespace: 'settings', key: 'docTitleAiAgents' },
      },
    },
    {
      path: 'support/help',
      element: wrap(AccountHelpPage),
      handle: { documentTitle: { namespace: 'settings', key: 'docTitleHelp' } },
    },
  ],
}
